{
  "$description": "Token semantici di colore di MovoFleet. Ogni foglia ha un valore chiaro e uno scuro; buildTheme.ts ne genera le variabili CSS e le utility Tailwind. Una foglia può avere figli (emphasis → emphasis.muted).",
  "color": {
    "bg": {
      "canvas": {
        "light": "#eef4fb",
        "dark": "#0f172a",
        "description": "Sfondo della shell dell'app."
      },
      "default": {
        "light": "#ffffff",
        "dark": "#111827",
        "description": "Card, pannelli, popover."
      },
      "subtle": {
        "light": "#f8fafc",
        "dark": "#0b1220",
        "description": "Canvas delle liste, intestazioni di tabella, campo di ricerca."
      },
      "muted": {
        "light": "#f1f5f9",
        "dark": "#1e293b",
        "description": "Chip neutri, hover dei controlli secondari."
      },
      "strong": {
        "light": "#e2e8f0",
        "dark": "#273449",
        "description": "Riempimento neutro marcato: hover dei chip neutri, binari delle barre di avanzamento, linee della timeline, divisori verticali."
      },
      "disabled": {
        "light": "#cbd5e1",
        "dark": "#334155",
        "description": "Fondo dei bottoni disabilitati."
      },
      "neutral": {
        "emphasis": {
          "light": "#94a3b8",
          "dark": "#475569",
          "description": "Pallino neutro pieno: stato inattivo nelle timeline."
        }
      },
      "hover": {
        "light": "#eff6ff",
        "dark": "#162540",
        "description": "Riga in hover o selezionata."
      },
      "emphasis": {
        "light": "#0f172a",
        "dark": "#334155",
        "description": "Pannelli navy, tessera KPI attiva.",
        "muted": {
          "light": "#1e293b",
          "dark": "#334155",
          "description": "Secondo livello navy."
        }
      },
      "primary": {
        "light": "#2563eb",
        "dark": "#2563eb",
        "description": "Bottone primario.",
        "hover": {
          "light": "#1d4ed8",
          "dark": "#1d4ed8",
          "description": "Hover del bottone primario."
        }
      },
      "brand": {
        "light": "#1e88e5",
        "dark": "#1e88e5",
        "description": "Pill attiva, riempimenti del brand.",
        "hover": {
          "light": "#1976d2",
          "dark": "#1976d2",
          "description": "Hover dei riempimenti del brand."
        }
      },
      "selected": {
        "light": "#1976d2",
        "dark": "#1976d2",
        "description": "Pill, tab e pagina attivi con testo bianco (4.6:1); bg-brand resta per i riempimenti senza testo."
      },
      "info": {
        "subtle": {
          "light": "#eff6ff",
          "dark": "#162540",
          "description": "Fondo dei badge informativi."
        },
        "muted": {
          "light": "#dbeafe",
          "dark": "#192b4c",
          "description": "Tinta informativo più piena: hover dei bottoni tinti, avatar."
        },
        "emphasis": {
          "light": "#2563eb",
          "dark": "#2563eb",
          "description": "Pallino, barra, marker informativo."
        }
      },
      "success": {
        "subtle": {
          "light": "#ecfdf5",
          "dark": "#112b32",
          "description": "Fondo dei badge di successo."
        },
        "muted": {
          "light": "#d1fae5",
          "dark": "#113537",
          "description": "Tinta di successo più piena: hover dei bottoni tinti, avatar."
        },
        "emphasis": {
          "light": "#047857",
          "dark": "#047857",
          "description": "Pallino, barra, marker di successo."
        }
      },
      "warning": {
        "subtle": {
          "light": "#fffbeb",
          "dark": "#2c2824",
          "description": "Fondo dei badge di attenzione."
        },
        "muted": {
          "light": "#fef3c7",
          "dark": "#3a3022",
          "description": "Tinta di attenzione più piena: hover dei bottoni tinti, avatar."
        },
        "emphasis": {
          "light": "#b45309",
          "dark": "#b45309",
          "description": "Pallino, barra, marker di attenzione."
        }
      },
      "danger": {
        "subtle": {
          "light": "#fff1f2",
          "dark": "#2c1d2e",
          "description": "Fondo dei badge di pericolo."
        },
        "muted": {
          "light": "#ffe4e6",
          "dark": "#3a1f31",
          "description": "Tinta di pericolo più piena: hover dei bottoni tinti, avatar."
        },
        "emphasis": {
          "light": "#e11d48",
          "dark": "#e11d48",
          "description": "Pallino, barra, marker di pericolo."
        }
      },
      "backdrop": {
        "light": "#020617",
        "dark": "#0f172a",
        "description": "Velo dietro drawer e dialoghi, da usare con opacità (bg-backdrop/50)."
      },
      "topbar": {
        "light": "#ffffff",
        "dark": "#0f172a",
        "description": "Superficie della top bar della shell (token di componente)."
      },
      "control": {
        "light": "#ffffff",
        "dark": "#334155",
        "description": "Fondo dei controlli della top bar: pulsanti icona, apertura della sidebar.",
        "hover": {
          "light": "#f8fafc",
          "dark": "#334155",
          "description": "Hover dei controlli trasparenti della top bar."
        }
      },
      "overlay": {
        "light": "#ffffff",
        "dark": "#182131",
        "description": "Superfici sovrapposte: menu, tendine, popover, tooltip, modali. In scuro un gradino sopra le card (bg.default), come l'elevazione di Atlassian e Primer.",
        "hover": {
          "light": "#f1f5f9",
          "dark": "#1e293b",
          "description": "Hover di una voce in un menu o in una tendina."
        }
      }
    },
    "text": {
      "strong": {
        "light": "#0f172a",
        "dark": "#f1f5f9",
        "description": "Titoli, valori, intestazioni."
      },
      "default": {
        "light": "#334155",
        "dark": "#cbd5e1",
        "description": "Testo del corpo."
      },
      "muted": {
        "light": "#475569",
        "dark": "#afb2b6",
        "description": "Sottotitoli, etichette secondarie."
      },
      "subtle": {
        "light": "#5d6b82",
        "dark": "#94a3b8",
        "description": "Didascalie, meta, etichette dei KPI."
      },
      "placeholder": {
        "light": "#7d8ba1",
        "dark": "#64748b",
        "description": "Placeholder e icone a riposo. Solo icone, mai testo da leggere."
      },
      "disabled": {
        "light": "#cbd5e1",
        "dark": "#475569",
        "description": "Testo e icone dei controlli disabilitati. Esente dal contrasto minimo (WCAG 1.4.3)."
      },
      "onEmphasis": {
        "light": "#ffffff",
        "dark": "#ffffff",
        "description": "Testo su riempimenti primary, brand, navy e status emphasis."
      },
      "link": {
        "light": "#1d4ed8",
        "dark": "#93c5fd",
        "description": "Link, eyebrow, azioni inline."
      },
      "brand": {
        "light": "#1565c0",
        "dark": "#60a5fa",
        "description": "Etichetta del tab attivo, filtro selezionato."
      },
      "info": {
        "light": "#1d4ed8",
        "dark": "#93c5fd",
        "description": "Etichetta informativa."
      },
      "success": {
        "light": "#047857",
        "dark": "#6ee7b7",
        "description": "Etichetta di successo."
      },
      "warning": {
        "light": "#92400e",
        "dark": "#fcd34d",
        "description": "Etichetta di attenzione."
      },
      "danger": {
        "light": "#be123c",
        "dark": "#fda4af",
        "description": "Etichetta di pericolo."
      },
      "control": {
        "light": "#334155",
        "dark": "#f1f5f9",
        "description": "Testo e icone dei controlli e delle voci di menu della top bar."
      }
    },
    "border": {
      "default": {
        "light": "#e2e8f0",
        "dark": "#29313d",
        "description": "Card, input, divisori."
      },
      "subtle": {
        "light": "#f1f5f9",
        "dark": "#273449",
        "description": "Divisori interni."
      },
      "strong": {
        "light": "#cbd5e1",
        "dark": "#334155",
        "description": "Divisori marcati."
      },
      "emphasis": {
        "light": "#94a3b8",
        "dark": "#475569",
        "description": "Bordo neutro marcato: stato attivo dei filtri neutri."
      },
      "hover": {
        "light": "#bfdbfe",
        "dark": "#203d6f",
        "description": "Hover di card e controlli."
      },
      "brand": {
        "light": "#1e88e5",
        "dark": "#1e88e5",
        "description": "Sottolineatura del tab attivo, binario selezionato."
      },
      "focus": {
        "light": "#1e88e5",
        "dark": "#60a5fa",
        "description": "Anello e outline di focus."
      },
      "info": {
        "muted": {
          "light": "#bfdbfe",
          "dark": "#203d6f",
          "description": "Bordo dei badge informativi."
        },
        "emphasis": {
          "light": "#2563eb",
          "dark": "#2563eb",
          "description": "Bordo informativo pieno: filtro attivo, opzione selezionata."
        }
      },
      "success": {
        "muted": {
          "light": "#a7f3d0",
          "dark": "#115047",
          "description": "Bordo dei badge di successo."
        },
        "emphasis": {
          "light": "#047857",
          "dark": "#047857",
          "description": "Bordo di successo pieno: filtro attivo, opzione selezionata."
        }
      },
      "warning": {
        "muted": {
          "light": "#fde68a",
          "dark": "#61471d",
          "description": "Bordo dei badge di attenzione."
        },
        "emphasis": {
          "light": "#b45309",
          "dark": "#b45309",
          "description": "Bordo di attenzione pieno: filtro attivo, opzione selezionata."
        }
      },
      "danger": {
        "muted": {
          "light": "#fecdd3",
          "dark": "#60263a",
          "description": "Bordo dei badge di pericolo."
        },
        "emphasis": {
          "light": "#e11d48",
          "dark": "#e11d48",
          "description": "Bordo di pericolo pieno: filtro attivo, opzione selezionata."
        }
      },
      "topbar": {
        "light": "#e8edf3",
        "dark": "#273449",
        "description": "Bordo inferiore della top bar (token di componente)."
      },
      "control": {
        "light": "#e2e8f0",
        "dark": "#334155",
        "description": "Bordo dei controlli della top bar.",
        "hover": {
          "light": "#bfdbfe",
          "dark": "#2563eb",
          "description": "Hover dei controlli della top bar e dell'avatar."
        }
      },
      "overlay": {
        "light": "#e2e8f0",
        "dark": "#334155",
        "description": "Bordo delle superfici sovrapposte (menu, popover, modali)."
      }
    },
    "chart": {
      "1": {
        "light": "#1e88e5",
        "dark": "#1e88e5",
        "description": "Serie 1 dei grafici (blu del brand)."
      },
      "2": {
        "light": "#ea580c",
        "dark": "#ea580c",
        "description": "Serie 2 dei grafici (arancio)."
      },
      "3": {
        "light": "#0d9488",
        "dark": "#0d9488",
        "description": "Serie 3 dei grafici (verde acqua)."
      },
      "4": {
        "light": "#8b5cf6",
        "dark": "#8b5cf6",
        "description": "Serie 4 dei grafici (viola)."
      },
      "5": {
        "light": "#db2777",
        "dark": "#db2777",
        "description": "Serie 5 dei grafici (magenta)."
      },
      "6": {
        "light": "#a16207",
        "dark": "#a16207",
        "description": "Serie 6 dei grafici (ocra)."
      },
      "axis": {
        "light": "#5d6b82",
        "dark": "#94a3b8",
        "description": "Etichette e tacche degli assi (come text.subtle)."
      },
      "grid": {
        "light": "#e2e8f0",
        "dark": "#29313d",
        "description": "Griglia e linea di base dei grafici (come border.default)."
      }
    },
    "accent": {
      "navy": {
        "light": "#0f172a",
        "dark": "#0f172a",
        "description": "Decorativo: hero dei dettagli veicolo/conducente (centro), fondo della foto mancante.",
        "deep": {
          "light": "#020617",
          "dark": "#020617",
          "description": "Decorativo: hero dei dettagli (inizio del gradiente)."
        },
        "blue": {
          "light": "#0b1730",
          "dark": "#0b1730",
          "description": "Decorativo: hero dei dettagli (fine del gradiente)."
        },
        "soft": {
          "light": "#1e293b",
          "dark": "#1e293b",
          "description": "Decorativo: fondo della foto mancante (fine del gradiente)."
        }
      },
      "marine": {
        "light": "#0c2844",
        "dark": "#0c2844",
        "description": "Decorativo: pannello mappa del login (centro del gradiente).",
        "deep": {
          "light": "#071525",
          "dark": "#071525",
          "description": "Decorativo: pannello mappa del login (fondo e velature)."
        },
        "soft": {
          "light": "#143a5c",
          "dark": "#143a5c",
          "description": "Decorativo: pannello mappa del login (fine del gradiente)."
        }
      },
      "blue": {
        "light": "#3b82f6",
        "dark": "#3b82f6",
        "description": "Decorativo: bagliori blu degli hero, gradiente del form demo.",
        "soft": {
          "light": "#93c5fd",
          "dark": "#93c5fd",
          "description": "Decorativo: filo luminoso sotto il PageHeader."
        }
      },
      "sky": {
        "light": "#0ea5e9",
        "dark": "#0ea5e9",
        "description": "Decorativo: bagliore azzurro del PageHeader.",
        "soft": {
          "light": "#38bdf8",
          "dark": "#38bdf8",
          "description": "Decorativo: gradiente del form demo."
        },
        "pale": {
          "light": "#78c8ff",
          "dark": "#78c8ff",
          "description": "Decorativo: bagliori e tracce del pannello mappa del login."
        }
      },
      "cyan": {
        "light": "#22d3ee",
        "dark": "#22d3ee",
        "description": "Decorativo: bagliori ciano degli hero e del login.",
        "soft": {
          "light": "#67e8f9",
          "dark": "#67e8f9",
          "description": "Decorativo: gradiente del form demo."
        }
      },
      "amber": {
        "light": "#f59e0b",
        "dark": "#f59e0b",
        "description": "Decorativo: hero delle carte carburante (inizio), tracce calde del login.",
        "soft": {
          "light": "#fbbf24",
          "dark": "#fbbf24",
          "description": "Decorativo: bagliori caldi del login, pallino della finestra nella vetrina."
        }
      },
      "orange": {
        "light": "#f97316",
        "dark": "#f97316",
        "description": "Decorativo: hero delle carte carburante (centro)."
      },
      "red": {
        "light": "#ef4444",
        "dark": "#ef4444",
        "description": "Decorativo: hero delle carte carburante (fine).",
        "soft": {
          "light": "#f87171",
          "dark": "#f87171",
          "description": "Decorativo: pallino della finestra nella vetrina."
        }
      },
      "yellow": {
        "pale": {
          "light": "#fef08a",
          "dark": "#fef08a",
          "description": "Decorativo: icona e scintilla dell'hero delle carte carburante."
        }
      },
      "green": {
        "soft": {
          "light": "#34d399",
          "dark": "#34d399",
          "description": "Decorativo: pallino della finestra nella vetrina."
        }
      },
      "white": {
        "light": "#ffffff",
        "dark": "#ffffff",
        "description": "Decorativo: vetro, fili e bordi chiari su hero scuri o colorati."
      },
      "mist": {
        "light": "#eef3f8",
        "dark": "#eef3f8",
        "description": "Decorativo: dissolvenza in basso dell'hero dei dettagli."
      }
    }
  },
  "shadow": {
    "resting": {
      "light": "0 1px 2px 0 rgb(2 6 23 / 0.05)",
      "dark": "0 1px 2px 0 rgb(0 0 0 / 0.4)",
      "description": "Card, strisce, pill in quiete."
    },
    "raised": {
      "light": "0 4px 6px -1px rgb(15 23 42 / 0.1), 0 2px 4px -2px rgb(15 23 42 / 0.1)",
      "dark": "0 4px 6px -1px rgb(0 0 0 / 0.5), 0 2px 4px -2px rgb(0 0 0 / 0.5)",
      "description": "Tab e pulsanti attivi, menu a tendina, popover, hover delle card."
    },
    "overlay": {
      "light": "0 20px 25px -5px rgb(15 23 42 / 0.1), 0 8px 10px -6px rgb(15 23 42 / 0.1)",
      "dark": "0 20px 25px -5px rgb(0 0 0 / 0.6), 0 8px 10px -6px rgb(0 0 0 / 0.6)",
      "description": "Modali, drawer, sidebar sovrapposta, superfici hero."
    },
    "map": {
      "pill": {
        "light": "0 1px 3px rgb(0 0 0 / 0.18), 0 4px 12px -4px rgb(0 0 0 / 0.22), inset 0 1px 0 rgb(255 255 255 / 0.18)",
        "dark": "0 1px 3px rgb(0 0 0 / 0.18), 0 4px 12px -4px rgb(0 0 0 / 0.22), inset 0 1px 0 rgb(255 255 255 / 0.18)",
        "description": "Mappa live: etichetta del veicolo (token di componente; le tile restano chiare anche in scuro).",
        "raised": {
          "light": "0 2px 6px rgb(0 0 0 / 0.22), 0 8px 20px -6px rgb(0 0 0 / 0.28), inset 0 1px 0 rgb(255 255 255 / 0.22)",
          "dark": "0 2px 6px rgb(0 0 0 / 0.22), 0 8px 20px -6px rgb(0 0 0 / 0.28), inset 0 1px 0 rgb(255 255 255 / 0.22)",
          "description": "Mappa live: etichetta in hover o selezionata."
        }
      },
      "dot": {
        "light": "0 1px 4px rgb(0 0 0 / 0.25)",
        "dark": "0 1px 4px rgb(0 0 0 / 0.25)",
        "description": "Mappa live: pallino del veicolo, marker di inizio e fine percorso.",
        "raised": {
          "light": "0 2px 8px rgb(0 0 0 / 0.3)",
          "dark": "0 2px 8px rgb(0 0 0 / 0.3)",
          "description": "Mappa live: pallino in hover o selezionato."
        }
      },
      "glyph": {
        "light": "0 1px 1px rgb(0 0 0 / 0.15)",
        "dark": "0 1px 1px rgb(0 0 0 / 0.15)",
        "description": "Mappa live: frecce di direzione e targa (drop-shadow e text-shadow)."
      }
    }
  }
}
